package co.maytes.standupbot.dashboard.features.setup

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.lib.api.map
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.UiContainer
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.shared.dto.integration.GithubAuthorizeUrlResponseDto
import co.maytes.standupbot.shared.dto.integration.GoogleAuthorizeUrlResponseDto
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.integration.LinearAuthorizeUrlResponseDto
import kotlinx.browser.window

sealed interface ReconnectTarget {
    data class Redirect(val url: String) : ReconnectTarget

    data class AuthorizeUrl(val path: String, val provider: IntegrationProviderDto) : ReconnectTarget

    data class Navigate(val path: String) : ReconnectTarget
}

object ReconnectRoutes {
    const val HOME = "/app"
    const val INTEGRATIONS = "/app/settings/integrations"
    const val ANTHROPIC_KEY = "/app/settings/integrations?fix=anthropic"

    fun targetFor(
        provider: IntegrationProviderDto,
        workspaceId: String,
        returnTo: String,
    ): ReconnectTarget {
        val base = "/api/v1/workspaces/$workspaceId/integrations"
        return when (provider) {
            IntegrationProviderDto.SLACK -> ReconnectTarget.Redirect("/oauth/slack/install?returnTo=$returnTo")
            IntegrationProviderDto.LINEAR ->
                ReconnectTarget.AuthorizeUrl("$base/linear/authorize-url?returnTo=$returnTo", provider)
            IntegrationProviderDto.GOOGLE_CALENDAR ->
                ReconnectTarget.AuthorizeUrl("$base/google/authorize-url?returnTo=$returnTo", provider)
            IntegrationProviderDto.GITHUB -> ReconnectTarget.AuthorizeUrl("$base/github/authorize-url", provider)
            IntegrationProviderDto.ANTHROPIC, IntegrationProviderDto.UNKNOWN -> ReconnectTarget.Navigate(ANTHROPIC_KEY)
        }
    }

    fun returnPathFor(currentPath: String): String = currentPath.takeIf { it == HOME || it.startsWith("$HOME/") } ?: HOME

    fun failureMessage(
        providerName: String,
        reason: String,
    ): String = "Couldn't start the $providerName reconnect: $reason"
}

class ReconnectLauncher(
    private val api: ApiClient,
    private val ui: UiContainer,
    private val redirect: (String) -> Unit = { window.location.href = it },
) {
    suspend fun launch(
        provider: IntegrationProviderDto,
        workspaceId: String,
        providerName: String,
        returnTo: String = ReconnectRoutes.HOME,
        navigate: (String) -> Unit,
    ): Boolean =
        when (val target = ReconnectRoutes.targetFor(provider, workspaceId, returnTo)) {
            is ReconnectTarget.Redirect -> true.also { redirect(target.url) }
            is ReconnectTarget.Navigate -> true.also { navigate(target.path) }
            is ReconnectTarget.AuthorizeUrl -> authorize(target, providerName)
        }

    private suspend fun authorize(
        target: ReconnectTarget.AuthorizeUrl,
        providerName: String,
    ): Boolean {
        val url = fetchUrl(target)
        return when (url) {
            is Result.Ok -> true.also { redirect(url.value) }
            is Result.Err -> false.also { ui.showToast(ReconnectRoutes.failureMessage(providerName, url.error.displayMessage), ToastKind.Error) }
        }
    }

    private suspend fun fetchUrl(target: ReconnectTarget.AuthorizeUrl): Result<String, ApiError> =
        when (target.provider) {
            IntegrationProviderDto.LINEAR -> api.get<LinearAuthorizeUrlResponseDto>(target.path).map { it.url }
            IntegrationProviderDto.GOOGLE_CALENDAR -> api.get<GoogleAuthorizeUrlResponseDto>(target.path).map { it.url }
            else -> api.get<GithubAuthorizeUrlResponseDto>(target.path).map { it.url }
        }
}

@Composable
fun rememberReconnectLauncher(): ReconnectLauncher {
    val api = rememberApiClient()
    val ui = rememberUiStore()
    return remember(api, ui) { ReconnectLauncher(api, ui) }
}
